import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { contractorUploadAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  Building2, 
  Upload, 
  CheckCircle2, 
  AlertTriangle,
  Loader2,
  Camera,
  Receipt,
  FileText,
  Lock
} from 'lucide-react';
import { toast } from 'sonner';

const ContractorUploadPage = () => {
  const { portalId } = useParams();
  const [searchParams] = useSearchParams();
  const codeFromUrl = searchParams.get('code') || '';
  
  const [accessCode, setAccessCode] = useState(codeFromUrl);
  const [verified, setVerified] = useState(false);
  const [portalInfo, setPortalInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  
  // Upload state
  const [uploadType, setUploadType] = useState('photo');
  const [description, setDescription] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);

  useEffect(() => {
    if (codeFromUrl && portalId) {
      handleVerify();
    }
  }, []);

  const handleVerify = async () => {
    if (!accessCode) {
      toast.error('Please enter the access code');
      return;
    }

    setVerifyLoading(true);
    try {
      const response = await contractorUploadAPI.verify(portalId, accessCode);
      setPortalInfo(response.data);
      setVerified(true);
      toast.success('Access verified!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid access code');
      setVerified(false);
    } finally {
      setVerifyLoading(false);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select a file');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('code', accessCode);
      formData.append('upload_type', uploadType);
      formData.append('description', description);

      await contractorUploadAPI.upload(portalId, formData);
      
      setUploadSuccess(true);
      setSelectedFile(null);
      setDescription('');
      toast.success('Upload successful!');
      
      // Reset success state after 3 seconds
      setTimeout(() => setUploadSuccess(false), 3000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setLoading(false);
    }
  };

  const getUploadIcon = (type) => {
    switch (type) {
      case 'photo': return <Camera className="w-5 h-5" />;
      case 'receipt': return <Receipt className="w-5 h-5" />;
      case 'permit': return <FileText className="w-5 h-5" />;
      default: return <Upload className="w-5 h-5" />;
    }
  };

  if (!verified) {
    return (
      <div className="min-h-screen grid-bg flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-border bg-card rounded-sm">
          <CardHeader className="text-center">
            <div className="w-16 h-16 rounded-sm bg-primary/10 border border-primary/30 flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-primary" />
            </div>
            <CardTitle className="text-2xl font-bold font-heading">Contractor Portal</CardTitle>
            <CardDescription>Enter your access code to upload documents</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Access Code</Label>
              <Input
                value={accessCode}
                onChange={(e) => setAccessCode(e.target.value.toUpperCase())}
                placeholder="Enter code (e.g., ABC12345)"
                className="bg-input/50 border-border focus:border-primary rounded-sm h-11 text-center text-lg font-mono tracking-wider"
                maxLength={8}
                data-testid="access-code-input"
              />
            </div>
            <Button
              onClick={handleVerify}
              disabled={verifyLoading || !accessCode}
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm h-11"
              data-testid="verify-btn"
            >
              {verifyLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Verifying...
                </>
              ) : (
                'Verify Access'
              )}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid-bg p-4">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <Card className="border-border bg-card rounded-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-sm bg-primary/10 flex items-center justify-center">
                <Building2 className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h1 className="text-xl font-bold font-heading">{portalInfo?.project_name}</h1>
                <p className="text-sm text-muted-foreground">{portalInfo?.project_address}</p>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-border flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Contractor</span>
              <Badge variant="outline" className="rounded-sm">{portalInfo?.contractor_name}</Badge>
            </div>
          </CardContent>
        </Card>

        {/* Upload Form */}
        <Card className="border-border bg-card rounded-sm">
          <CardHeader>
            <CardTitle className="font-heading">Upload Document</CardTitle>
            <CardDescription>Upload photos, receipts, or permit documents</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Upload Type Selection */}
            <div className="grid grid-cols-3 gap-3">
              {[
                { value: 'photo', label: 'Photo', icon: Camera },
                { value: 'receipt', label: 'Receipt', icon: Receipt },
                { value: 'permit', label: 'Permit', icon: FileText },
              ].map((type) => (
                <button
                  key={type.value}
                  onClick={() => setUploadType(type.value)}
                  className={`p-4 rounded-sm border transition-all ${
                    uploadType === type.value 
                      ? 'border-primary bg-primary/10 text-primary' 
                      : 'border-border hover:border-primary/50'
                  }`}
                  data-testid={`upload-type-${type.value}`}
                >
                  <type.icon className="w-6 h-6 mx-auto mb-2" />
                  <span className="text-sm font-medium">{type.label}</span>
                </button>
              ))}
            </div>

            {/* File Input */}
            <div className="space-y-2">
              <Label>Select File</Label>
              <div className="border-2 border-dashed border-border rounded-sm p-6 text-center hover:border-primary/50 transition-colors">
                <input
                  type="file"
                  onChange={(e) => setSelectedFile(e.target.files[0])}
                  className="hidden"
                  id="file-upload"
                  accept={uploadType === 'photo' ? 'image/*' : '*'}
                  data-testid="file-input"
                />
                <label htmlFor="file-upload" className="cursor-pointer">
                  {selectedFile ? (
                    <div className="flex items-center justify-center gap-2">
                      <CheckCircle2 className="w-5 h-5 text-green-400" />
                      <span className="text-sm">{selectedFile.name}</span>
                    </div>
                  ) : (
                    <div>
                      <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                      <p className="text-sm text-muted-foreground">
                        Click to select a file
                      </p>
                    </div>
                  )}
                </label>
              </div>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label>Description (optional)</Label>
              <Input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="e.g., Kitchen - Day 3"
                className="bg-input/50 border-border focus:border-primary rounded-sm"
                data-testid="description-input"
              />
            </div>

            {/* Upload Button */}
            <Button
              onClick={handleUpload}
              disabled={loading || !selectedFile}
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm h-12"
              data-testid="upload-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : uploadSuccess ? (
                <>
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Uploaded!
                </>
              ) : (
                <>
                  {getUploadIcon(uploadType)}
                  <span className="ml-2">Upload {uploadType.charAt(0).toUpperCase() + uploadType.slice(1)}</span>
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Instructions */}
        <Card className="border-border bg-card rounded-sm">
          <CardContent className="p-4">
            <h3 className="font-semibold mb-2 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-400" />
              Instructions
            </h3>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• Upload progress photos to document your work</li>
              <li>• Submit receipts for materials and supplies</li>
              <li>• Upload permit documents when received</li>
              <li>• Add descriptions to help identify uploads</li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ContractorUploadPage;
